// Persistent (on-device, cross-sitting) record of which Game-of-24 puzzles this browser has
// been served. Two uses:
//   1. the test EXTENSION offers only Wurgaft problems the user hasn't seen (unseen-first),
//      so continuers accumulate more coverage of the same 200 across sittings.
//   2. every trace is flagged first-exposure vs repeat, so the clean per-problem analysis can
//      restrict to first exposures (Wurgaft participants each saw every problem once).
//
// localStorage-scoped by design: we have no login, so a cleared cache is simply a new
// anonymous user starting fresh — which is the correct behaviour, not a failure mode.

const KEY = 'labs.seenPuzzles.v1'

// A puzzle's identity is its multiset of numbers (order-independent) — matches keyOf in
// game24/puzzles.ts, so a problem counts as "seen" regardless of presentation order.
export const puzzleKey = (numbers: number[]): string => [...numbers].sort((a, b) => a - b).join(',')

const load = (): Set<string> => {
  try {
    const raw = localStorage.getItem(KEY)
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

export const seenPuzzleKeys = (): Set<string> => load()

export const hasSeenPuzzle = (numbers: number[]): boolean => load().has(puzzleKey(numbers))

export const markPuzzleSeen = (numbers: number[]): void => {
  try {
    const s = load()
    s.add(puzzleKey(numbers))
    localStorage.setItem(KEY, JSON.stringify(Array.from(s)))
  } catch {
    /* storage unavailable (private mode / quota) — degrade gracefully: the puzzle just
       counts as unseen next time, which is harmless. */
  }
}
